import React from 'react';
import { Box, Container, Typography, Button, Stack, Divider } from '@mui/material';
import Header from '../Header';
import SEO from '../SEO';
import Footer from '../Footer';
import { useNavigate } from 'react-router-dom';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 6 }}>
      <Typography variant="h3" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: { xs: '1.4rem', md: '1.8rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 2.5 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function Para({ children }: { children: React.ReactNode }) {
  return (
    <Typography sx={{ fontFamily: '"Libre Baskerville", Georgia, serif', fontSize: { xs: 15, md: 17 }, color: '#9499b0', lineHeight: 1.8, mb: 2 }}>
      {children}
    </Typography>
  );
}

function StatBox({ amount, label, source }: { amount: string; label: string; source: string }) {
  return (
    <Box sx={{ p: 3, bgcolor: '#13131f', border: '1px solid rgba(92,225,230,0.2)', borderRadius: 2, textAlign: 'center' }}>
      <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: '2.2rem', color: '#5ce1e6', lineHeight: 1, mb: 0.5 }}>{amount}</Typography>
      <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 14, color: '#f0f0f8', mb: 0.5 }}>{label}</Typography>
      <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, color: 'rgba(148,153,176,0.6)' }}>{source}</Typography>
    </Box>
  );
}

export default function BlogHarveyMucklebustPage() {
  const navigate = useNavigate();

  return (
    <Box sx={{ bgcolor: '#0a0a12', minHeight: '100vh' }}>
      <SEO title="Rocking Horse Ranch — The Harvey Mucklebust Case" description="A detailed case study of repeated abuse at a licensed childcare facility and the monitoring failures that allowed it to continue undetected." path="/blog/rocking-horse-ranch-harvey-mucklebust" type="article" />
      <Header onBookDemo={() => navigate('/contact')} />
      <Container maxWidth="sm" sx={{ py: { xs: 6, md: 10 }, px: 3 }}>

        {/* Breadcrumb */}
        <Box sx={{ display: 'flex', gap: 1, mb: 4, alignItems: 'center' }}>
          <Typography onClick={() => navigate('/blog')} sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', cursor: 'pointer', '&:hover': { color: '#5ce1e6' } }}>Blog</Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: 'rgba(148,153,176,0.4)' }}>›</Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0' }}>Behavioral AI</Typography>
        </Box>

        {/* Tag badge */}
        <Box sx={{ px: 1.5, py: 0.5, bgcolor: 'rgba(92,225,230,0.08)', border: '1px solid rgba(92,225,230,0.2)', borderRadius: 1, display: 'inline-block', mb: 2.5 }}>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 11, color: '#5ce1e6', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Behavioral AI · 7 min read</Typography>
        </Box>

        <Typography variant="h1" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.8rem' }, letterSpacing: '-0.03em', color: '#f0f0f8', mb: 3, lineHeight: 1.1 }}>
          Three Incidents. One Worker. Three Weeks. How Behavioral AI Detects the Pattern Cameras Alone Miss.
        </Typography>
        <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 16, color: '#9499b0', mb: 5, lineHeight: 1.65 }}>
          Harvey Muklebust was 11 months old when he died at a Savage, Minnesota daycare on September 22, 2025. The worker who killed him had been hired three weeks earlier — and had already harmed a 4-month-old twice before Harvey ever entered the room. The pattern existed. No system was watching for it.
        </Typography>

        {/* YouTube embed */}
        <Box sx={{ position: 'relative', paddingTop: '56.25%', borderRadius: 2, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)', mb: 2, boxShadow: '0 0 40px rgba(0,0,0,0.4)' }}>
          <Box
            component="iframe"
            src="https://www.youtube.com/embed/nRmvqGGVTks"
            title="Harvey Muklebust — Rocking Horse Ranch daycare case coverage"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
          />
        </Box>
        <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12, color: 'rgba(148,153,176,0.5)', mb: 5, textAlign: 'center' }}>
          News coverage of the Rocking Horse Ranch case, Savage, Minnesota — September 2025
        </Typography>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mb: 5 }} />

        {/* Stats strip */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 6 }}>
          <StatBox amount="3 wks" label="Russell employed before Harvey's death" source="Rocking Horse Ranch, Savage MN" />
          <StatBox amount="3" label="Incidents — all linked to one worker" source="Sept 19 & 22, 2025" />
          <StatBox amount="4 mo." label="Age of first victim, P.K." source="Born April 21, 2025" />
        </Stack>

        <Section title="September 19, 2025 — the first warning">
          <Para>
            Three days before Harvey Muklebust died, Savage police were called to Rocking Horse Ranch childcare center on a different emergency. A 4-month-old infant — identified in court documents as P.K., born April 21, 2025 — had been found unresponsive. The baby had blood under their nose and mouth.
          </Para>
          <Para>
            P.K. survived. The incident was recorded. The daycare knew about it.
          </Para>
          <Para>
            The worker who had last contact with P.K. before that incident was Theah Loudemia Russell, 18 years old, of Savage, Minnesota. She had been employed at Rocking Horse Ranch for two and a half weeks.
          </Para>
          <Para>
            On September 22, 2025 — three days later, and two hours before Harvey Muklebust was found not breathing — P.K. was found unresponsive again. Same child. Same circumstances. Same worker as the last person to have contact.
          </Para>
          <Para>
            Two hours after that, at 11:34 AM on September 22, Harvey Muklebust was declared dead.
          </Para>
        </Section>

        <Section title="Harvey Gene Muklebust — September 22, 2025">
          <Para>
            Harvey Gene Muklebust was born September 27, 2024. He was 11 months old when he died — five days before his first birthday.
          </Para>
          <Para>
            Theah Russell called 911 to report Harvey was not breathing. Paramedics responded. He was taken to hospital and did not survive. The Scott County medical examiner performed an autopsy the following day, September 23, 2025, and determined the cause of death: homicide by suffocation.
          </Para>
          <Para>
            Savage Police Chief Brady Juell later stated that Russell "was involved in all three incidents, had last contact with the children before the incidents occurred, and exhibited suspicious behavior in the immediate aftermath."
          </Para>

          <Box sx={{ p: 2.5, bgcolor: '#13131f', border: '1px solid rgba(92,225,230,0.15)', borderLeft: '3px solid #5ce1e6', borderRadius: 2, mb: 3 }}>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 14, color: '#f0f0f8', mb: 0.75 }}>
              Three incidents. One common denominator.
            </Typography>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.6 }}>
              Theah Russell was the last person with each infant before each incident, across all three medical emergencies — two involving P.K., one fatal for Harvey. Police identified her as the pattern before she confessed.
            </Typography>
          </Box>
        </Section>

        <Section title="The confession — January 13, 2026">
          <Para>
            Russell was arrested on January 13, 2026, nearly four months after Harvey's death. During police questioning, she confessed to all three incidents.
          </Para>
          <Para>
            She admitted to placing her fingers down Harvey's throat — choking and suffocating him until he stopped breathing. She described carrying out the same act against P.K. on both September 19 and September 22. Her stated motive: to seek attention.
          </Para>
          <Para>
            Police Chief Juell confirmed: "Theah Russell has confessed to the attempted murder of our first victim on two occasions and to the murder of Harvey Muklebust."
          </Para>
          <Para>
            On April 8, 2026, a grand jury indicted Russell on four counts: first-degree premeditated murder; first-degree murder while committing child abuse with a prior pattern of child abuse; and two counts of first-degree assault causing great bodily harm. She was 18 years old at the time of the crimes, having been born February 3, 2007.
          </Para>

          {/* Charges breakdown */}
          <Stack spacing={1.5} sx={{ mb: 3 }}>
            {[
              { label: 'Count 1', detail: 'Premeditated murder in the first degree — Harvey Gene Muklebust' },
              { label: 'Count 2', detail: 'First degree murder while committing child abuse with a past pattern of child abuse — Harvey Gene Muklebust' },
              { label: 'Count 3', detail: 'Assault in the first degree (great bodily harm) — P.K., September 19, 2025' },
              { label: 'Count 4', detail: 'Assault in the first degree (great bodily harm) — P.K., September 22, 2025' },
            ].map(({ label, detail }) => (
              <Box key={label} sx={{ display: 'flex', gap: 2, p: 2, bgcolor: '#13131f', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2 }}>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 12, color: '#5ce1e6', flexShrink: 0, minWidth: 60 }}>{label}</Typography>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.6 }}>{detail}</Typography>
              </Box>
            ))}
          </Stack>

          <Para>
            The Minnesota Department of Children, Youth and Families suspended the license of Rocking Horse Ranch immediately following Harvey's death on September 22, 2025.
          </Para>
        </Section>

        <Section title="What was known about Russell before she was hired">
          <Para>
            After the arrest, investigators documented what was described as a "documented history of attention-seeking behavior." This included making 911 calls for nonsensical, fabricated incidents; involvement in fire-setting incidents at a church camp; and a pattern of erratic behavior toward other children.
          </Para>
          <Para>
            None of this was surfaced during her three weeks at Rocking Horse Ranch. She was hired. She was assigned to the infant room. She was left alone with babies.
          </Para>
          <Para>
            This is the hiring-and-monitoring gap that runs through every case like this one: background checks catch criminal records. They do not catch patterns of behavior. They do not catch an 18-year-old with a history of seeking attention through crisis who has now found herself in charge of infants who cannot speak, cannot report, and cannot protect themselves.
          </Para>
        </Section>

        <Section title="This is what behavioral pattern detection is built for">
          <Para>
            The Rocking Horse Ranch case is categorically different from a case where cameras would have caught the abuse directly. Suffocation of an infant by inserting fingers into the throat takes seconds. It can happen in a blind spot. It can happen in a moment when a camera's angle shows nothing definitive. The act itself may not be what an AI flags first.
          </Para>
          <Para>
            What AI flags is the pattern.
          </Para>

          <Stack spacing={2} sx={{ mb: 3 }}>
            {[
              {
                signal: 'Incident correlation by worker',
                detail: "After P.K.'s first unresponsive event on September 19, a behavioral AI system tracking which workers were present for each infant medical event would have flagged Russell as the common denominator within hours. Not after three incidents. After one.",
              },
              {
                signal: 'Suspicious post-incident behavior',
                detail: 'Police Chief Juell noted Russell "exhibited suspicious behavior in the immediate aftermath" of each incident. Behavioral AI analyzing worker movement, proximity, and response patterns after a child distress event surfaces exactly this kind of anomaly.',
              },
              {
                signal: 'Escalating distress events in a single worker\'s room',
                detail: "Two medical emergencies in three days, both in Russell's care, both involving the same infant. An alert pattern: this room, this worker, this infant — three times in four days.",
              },
              {
                signal: 'New-hire behavioral baseline deviation',
                detail: "Russell had worked at Rocking Horse Ranch for three weeks. A system establishing behavioral baselines for new workers would have had enough data by week two to identify anomalies in her proximity patterns around infants, her response behavior during distress events, and her post-incident conduct.",
              },
            ].map(({ signal, detail }) => (
              <Box key={signal} sx={{ p: 2.5, bgcolor: '#13131f', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2 }}>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 13, color: '#5ce1e6', mb: 0.75 }}>{signal}</Typography>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.6 }}>{detail}</Typography>
              </Box>
            ))}
          </Stack>

          <Para>
            This is the scenario: September 19, P.K. is found unresponsive. Russell is the last-contact worker. SMCLA flags the incident and tags Russell as the attending worker. The owner is alerted — not just to the emergency, but to a behavioral data point. One incident, one worker.
          </Para>
          <Para>
            September 22, P.K. is found unresponsive again. SMCLA flags the correlation immediately: same worker, same infant, second event in three days. The owner receives an alert before the second emergency of the day even begins. Harvey never goes into that room with Theah Russell. Harvey goes home.
          </Para>
        </Section>

        <Section title="The hardest truth in this case">
          <Para>
            Theah Russell had a documented history of attention-seeking behavior before she walked into Rocking Horse Ranch. She had made false 911 calls. She had set fires. She had behaved erratically around children. None of that was in a criminal database. All of it was part of a behavioral profile that a screening system — or a supervisor paying attention to early warning signs in her first weeks — might have surfaced.
          </Para>
          <Para>
            And then there were three warning events. P.K. on September 19. P.K. again on September 22, two hours before Harvey. Each one was a data point. Each one pointed to the same worker. No system connected them in real time.
          </Para>
          <Para>
            Harvey Muklebust was born September 27, 2024. He died September 22, 2025 — five days before his first birthday. He had been alive for less than a year. The person who killed him had been employed at his daycare for three weeks.
          </Para>
          <Para>
            Three weeks is long enough for a behavioral AI system to establish a baseline, detect anomalies, correlate incidents, and flag a worker who is the common denominator in multiple infant medical emergencies.
          </Para>
          <Para>
            It is also, as this case shows, long enough for the worst outcome imaginable.
          </Para>
        </Section>

        {/* Source attribution */}
        <Box sx={{ p: 2.5, bgcolor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2, mb: 5 }}>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 12, color: '#9499b0', mb: 1.5 }}>Sources</Typography>
          <Stack spacing={1}>
            {[
              { label: 'KSTP 5 Eyewitness News — arrest made in 2025 infant death at Savage day care', url: 'https://kstp.com/kstp-news/top-news/arrest-made-in-2025-infant-death-at-savage-day-care/' },
              { label: 'CBS Minnesota — 18-year-old Savage day care employee confessed to intentionally suffocating infant boy', url: 'https://www.cbsnews.com/minnesota/news/savage-rocking-horse-ranch-infant-death-arrest/' },
              { label: 'FOX 9 Minneapolis — Rocking Horse Ranch day care worker fatally suffocated infant for attention', url: 'https://www.fox9.com/news/rocking-horse-ranch-day-care-worker-fatally-strangled-infant-attention-police' },
              { label: 'KARE 11 — Savage day care worker charged with infant\'s choking death', url: 'https://www.kare11.com/article/news/local/savage-day-care-worker-charged-with-infants-choking-death/89-8d183bf4-2a3f-4b0b-b51b-6d52f2798652' },
              { label: 'Scott County MN — grand jury indictment of Theah Loudemia Russell, April 8, 2026', url: 'https://www.scottcountymn.gov/m/newsflash/home/detail/1873' },
              { label: 'Bring Me The News — daycare worker suffocated two babies in her care, killing one', url: 'https://bringmethenews.com/minnesota-news/charges-daycare-worker-suffocated-two-babies-in-her-care-killing-one' },
            ].map(({ label, url }) => (
              <Box key={url} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'rgba(148,153,176,0.4)', mt: 0.7, flexShrink: 0 }} />
                <Typography
                  component="a"
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12, color: '#9499b0', lineHeight: 1.5, textDecoration: 'none', '&:hover': { color: '#5ce1e6', textDecoration: 'underline' } }}
                >
                  {label}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mb: 5 }} />

        <Box sx={{ p: 4, bgcolor: '#13131f', border: '1px solid rgba(92,225,230,0.2)', borderRadius: 3, textAlign: 'center' }}>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: 20, color: '#f0f0f8', mb: 1 }}>
            Pattern detection starts on day one
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', mb: 3, lineHeight: 1.65 }}>
            Theah Russell's pattern was visible after one incident. SMCLA correlates worker presence with infant distress events in real time — so the second event triggers an alert, not a death certificate.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/contact')} sx={{ px: 4, py: 1.5, fontWeight: 700, fontSize: 15, bgcolor: '#5ce1e6', color: '#0a0a12', borderRadius: 2, '&:hover': { bgcolor: '#4ecfd4' } }}>
            Book a Demo →
          </Button>
        </Box>
      </Container>
      <Footer />
    </Box>
  );
}
